import * as THREE from "three";
import type { VehicleBlueprint, RenderMode } from "@neon-arena/shared";

const _WHITE = new THREE.Color(1, 1, 1);

export interface VehicleVisual {
  group: THREE.Group;
  primary: THREE.Color;
  secondary: THREE.Color;
  trim: THREE.Color;
  bodyMat: THREE.MeshBasicMaterial;
  originalBodyColor: THREE.Color;
  flashTimer: number;
  zoneFrontMat: THREE.MeshBasicMaterial;
  zoneBodyMat:  THREE.MeshBasicMaterial;
  zoneRearMat:  THREE.MeshBasicMaterial;
  wheelGroups: THREE.Group[];
  wheelRadius: number;
}

export function triggerFlash(visual: VehicleVisual): void {
  visual.flashTimer = 0.18;
}

export function tickFlash(visual: VehicleVisual, dt: number): void {
  if (visual.flashTimer <= 0) return;
  visual.flashTimer = Math.max(0, visual.flashTimer - dt);
  const t = visual.flashTimer / 0.18;
  visual.bodyMat.color.copy(visual.originalBodyColor).lerp(_WHITE, t);
}

export function tickWheelSpin(visual: VehicleVisual, speed: number, locked: boolean, dt: number): void {
  if (locked || visual.wheelRadius <= 0) return;
  const omega = speed / visual.wheelRadius;
  for (const wg of visual.wheelGroups) wg.rotation.x += omega * dt;
}

/**
 * Update damage zone overlay opacity from server zone damage values.
 * Call each frame during remote sync and after receiving own player state.
 */
export function updateDamageZones(
  visual:      VehicleVisual,
  damageFront: number,
  damageBody:  number,
  damageRear:  number,
  maxHealth:   number,
): void {
  const s = maxHealth > 0 ? 1 / maxHealth : 0;
  visual.zoneFrontMat.opacity = Math.min(1, damageFront * s) * 0.70;
  visual.zoneBodyMat.opacity  = Math.min(1, damageBody  * s) * 0.70;
  visual.zoneRearMat.opacity  = Math.min(1, damageRear  * s) * 0.70;
}

export function buildVehicleVisual(bp: VehicleBlueprint, renderMode: RenderMode = "solid", streamsLevel = 0): VehicleVisual {
  const group = new THREE.Group();

  const primary = new THREE.Color(bp.style.primaryColor);
  const secondary = new THREE.Color(bp.style.secondaryColor);
  const trim = new THREE.Color(bp.style.trimColor);

  const bodyGeo = makeBodyGeometry(bp);
  const bodyMat = new THREE.MeshBasicMaterial({
    color: primary,
    transparent: renderMode === "wireframe",
    opacity: renderMode === "wireframe" ? 0.45 : 1.0,
  });
  const bodyMesh = new THREE.Mesh(bodyGeo, bodyMat);
  bodyMesh.position.y = bp.body.height / 2 + bp.wheels.size * 0.5;
  group.add(bodyMesh);

  // Toon mode uses trim colour for edges to give a bright neon-outline look
  const edgeColor = renderMode === "toon" ? trim : secondary;
  const edges = new THREE.LineSegments(
    new THREE.EdgesGeometry(bodyGeo, 18),
    new THREE.LineBasicMaterial({ color: edgeColor }),
  );
  edges.position.copy(bodyMesh.position);
  group.add(edges);

  // Emissive trim glow strips — bloom picks these up; toon gets stronger glow
  const stripGeo = new THREE.BoxGeometry(bp.body.width * 0.96, 0.05, bp.body.length * 0.92);
  const stripMat = new THREE.MeshStandardMaterial({
    color: trim,
    emissive: trim,
    emissiveIntensity: renderMode === "toon" ? 2.0 : 1.2,
    roughness: 1,
    metalness: 0,
  });
  const lowStrip = new THREE.Mesh(stripGeo, stripMat);
  lowStrip.position.y = bp.wheels.size * 0.5 + 0.05;
  group.add(lowStrip);

  // Emissive headlight bar
  const lightGeo = new THREE.BoxGeometry(bp.body.width * 0.6, 0.12, 0.08);
  const lightMat = new THREE.MeshStandardMaterial({
    color: trim,
    emissive: trim,
    emissiveIntensity: renderMode === "toon" ? 2.5 : 1.5,
    roughness: 1,
    metalness: 0,
  });
  const light = new THREE.Mesh(lightGeo, lightMat);
  light.position.set(0, bp.body.height * 0.6 + bp.wheels.size * 0.5, bp.body.length / 2 - 0.05);
  group.add(light);

  // weapon mount
  if (bp.weapon.type === "long-range-rail") {
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.08, 0.08, bp.body.length * 0.55, 8),
      new THREE.MeshStandardMaterial({ color: secondary, emissive: trim, emissiveIntensity: 0.4, roughness: 1, metalness: 0 }),
    );
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, bp.body.height * 0.85 + bp.wheels.size * 0.5, bp.body.length / 2 + 0.4);
    group.add(barrel);
  } else if (bp.weapon.type === "heavy-cannon") {
    const turret = new THREE.Mesh(
      new THREE.CylinderGeometry(0.18, 0.22, 0.4, 10),
      new THREE.MeshBasicMaterial({ color: primary }),
    );
    turret.position.set(0, bp.body.height + bp.wheels.size * 0.5, 0);
    group.add(turret);
    const barrel = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.12, bp.body.length * 0.45, 10),
      new THREE.MeshBasicMaterial({ color: secondary }),
    );
    barrel.rotation.x = Math.PI / 2;
    barrel.position.set(0, bp.body.height + bp.wheels.size * 0.5, bp.body.length * 0.25);
    group.add(barrel);
  } else if (bp.weapon.type === "rapid-blaster") {
    for (const dx of [-0.2, 0.2]) {
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(0.07, 0.07, bp.body.length * 0.4, 8),
        new THREE.MeshStandardMaterial({ color: trim, emissive: trim, emissiveIntensity: 0.6, roughness: 1, metalness: 0 }),
      );
      barrel.rotation.x = Math.PI / 2;
      barrel.position.set(dx, bp.body.height * 0.5 + bp.wheels.size * 0.5, bp.body.length * 0.4);
      group.add(barrel);
    }
  } else {
    const barrelOffsets = streamsLevel >= 1 ? [-0.25, 0.25] : [0];
    for (const dx of barrelOffsets) {
      const barrel = new THREE.Mesh(
        new THREE.CylinderGeometry(streamsLevel >= 1 ? 0.07 : 0.1, streamsLevel >= 1 ? 0.07 : 0.1, bp.body.length * 0.42, 8),
        new THREE.MeshStandardMaterial({ color: trim, emissive: trim, emissiveIntensity: 0.8, roughness: 1, metalness: 0 }),
      );
      barrel.rotation.x = Math.PI / 2;
      barrel.position.set(dx, bp.body.height * 0.6 + bp.wheels.size * 0.5, bp.body.length * 0.35);
      group.add(barrel);
    }
  }

  if (bp.body.spoiler) {
    const sx = bp.body.width * 0.95;
    const spoilerStand1 = new THREE.Mesh(
      new THREE.BoxGeometry(0.08, 0.35, 0.08),
      new THREE.MeshBasicMaterial({ color: secondary }),
    );
    const spoilerStand2 = spoilerStand1.clone();
    const standY = bp.body.height + bp.wheels.size * 0.5 - 0.1;
    spoilerStand1.position.set(-sx * 0.4, standY, -bp.body.length / 2 + 0.1);
    spoilerStand2.position.set(sx * 0.4, standY, -bp.body.length / 2 + 0.1);
    group.add(spoilerStand1);
    group.add(spoilerStand2);

    const wing = new THREE.Mesh(
      new THREE.BoxGeometry(sx, 0.08, 0.45),
      new THREE.MeshStandardMaterial({ color: trim, emissive: trim, emissiveIntensity: 1.0, roughness: 1, metalness: 0 }),
    );
    wing.position.set(0, standY + 0.2, -bp.body.length / 2 + 0.1);
    group.add(wing);
  }

  // wheels
  const wheelGeo = new THREE.CylinderGeometry(bp.wheels.size, bp.wheels.size, bp.wheels.width, 14);
  wheelGeo.rotateZ(Math.PI / 2);
  const wheelMat = new THREE.MeshBasicMaterial({ color: 0x111111 });

  const halfL = bp.body.length / 2 - bp.wheels.size * 0.7;
  const halfW = bp.body.width / 2 + bp.wheels.width * 0.05;
  const wheelY = bp.wheels.size;
  const corners: Array<[number, number]> = [
    [-halfW, halfL],
    [halfW, halfL],
    [-halfW, -halfL],
    [halfW, -halfL],
  ];
  const wheelGroups: THREE.Group[] = [];
  for (const [wx, wz] of corners) {
    const wg = new THREE.Group();
    wg.position.set(wx, wheelY, wz);
    wg.add(new THREE.Mesh(wheelGeo, wheelMat));
    const spoke = new THREE.LineSegments(
      makeSpokeRimGeo(bp.wheels.size * 0.72, 8),
      new THREE.LineBasicMaterial({ color: trim }),
    );
    spoke.position.x = Math.sign(wx) * (bp.wheels.width / 2 + 0.01);
    wg.add(spoke);
    group.add(wg);
    wheelGroups.push(wg);
  }


  // ── Damage zone overlays ────────────────────────────────────────────────
  // Three thin box overlays (front/body/rear thirds of the hull) that fade
  // from transparent → red as zone damage accumulates.  polygonOffset keeps
  // them from z-fighting with the body mesh beneath.
  const DAMAGE_COLOR = 0xff2020;
  const makeDmgMat = (): THREE.MeshBasicMaterial =>
    new THREE.MeshBasicMaterial({
      color: DAMAGE_COLOR,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -4,
    });

  const { width: bw, height: bh, length: bl } = bp.body;
  const zoneLen  = bl / 3;
  const zoneGeo  = new THREE.BoxGeometry(bw + 0.04, bh + 0.04, zoneLen + 0.02);
  const bodyY    = bh / 2 + bp.wheels.size * 0.5;

  const zoneFrontMat = makeDmgMat();
  const zoneBodyMat  = makeDmgMat();
  const zoneRearMat  = makeDmgMat();

  const zoneFront = new THREE.Mesh(zoneGeo, zoneFrontMat);
  const zoneBody  = new THREE.Mesh(zoneGeo, zoneBodyMat);
  const zoneRear  = new THREE.Mesh(zoneGeo, zoneRearMat);

  zoneFront.position.set(0, bodyY, +bl / 3);
  zoneBody.position.set(0, bodyY, 0);
  zoneRear.position.set(0, bodyY, -bl / 3);

  group.add(zoneFront, zoneBody, zoneRear);

  return {
    group,
    primary,
    secondary,
    trim,
    bodyMat,
    originalBodyColor: primary.clone(),
    flashTimer: 0,
    zoneFrontMat,
    zoneBodyMat,
    zoneRearMat,
    wheelGroups,
    wheelRadius: bp.wheels.size,
  };
}

function makeBodyGeometry(bp: VehicleBlueprint): THREE.BufferGeometry {
  const w = bp.body.width;
  const h = bp.body.height;
  const l = bp.body.length;

  switch (bp.body.shape) {
    case "wedge":
      return wedgeGeometry(w, h, l);
    case "muscle":
      return muscleGeometry(w, h, l);
    case "rocket":
      return rocketGeometry(w, h, l);
    case "buggy":
      return buggyGeometry(w, h, l);
    case "boxy":
    default:
      return new THREE.BoxGeometry(w, h, l);
  }
}

function wedgeGeometry(w: number, h: number, l: number): THREE.BufferGeometry {
  const hw = w / 2;
  const hh = h / 2;
  const hl = l / 2;
  const positions = new Float32Array([
    -hw, -hh, hl,  hw, -hh, hl,  hw, hh * 0.4, hl * 0.1,
    -hw, -hh, hl,  hw, hh * 0.4, hl * 0.1,  -hw, hh * 0.4, hl * 0.1,
    -hw, -hh, -hl,  -hw, hh, -hl * 0.6,  hw, hh, -hl * 0.6,
    -hw, -hh, -hl,  hw, hh, -hl * 0.6,  hw, -hh, -hl,
    -hw, hh * 0.4, hl * 0.1,  hw, hh * 0.4, hl * 0.1,  hw, hh, -hl * 0.6,
    -hw, hh * 0.4, hl * 0.1,  hw, hh, -hl * 0.6,  -hw, hh, -hl * 0.6,
    -hw, -hh, hl,  -hw, hh * 0.4, hl * 0.1,  -hw, hh, -hl * 0.6,
    -hw, -hh, hl,  -hw, hh, -hl * 0.6,  -hw, -hh, -hl,
    hw, -hh, hl,  hw, -hh, -hl,  hw, hh, -hl * 0.6,
    hw, -hh, hl,  hw, hh, -hl * 0.6,  hw, hh * 0.4, hl * 0.1,
    -hw, -hh, -hl,  hw, -hh, -hl,  hw, -hh, hl,
    -hw, -hh, -hl,  hw, -hh, hl,  -hw, -hh, hl,
  ]);
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geo.computeVertexNormals();
  return geo;
}

function muscleGeometry(w: number, h: number, l: number): THREE.BufferGeometry {
  return new THREE.BoxGeometry(w, h, l, 1, 1, 2);
}

function rocketGeometry(w: number, h: number, l: number): THREE.BufferGeometry {
  const geo = new THREE.CylinderGeometry(w * 0.4, w * 0.5, l, 8, 1, false);
  geo.rotateX(Math.PI / 2);
  geo.scale(1, h / (w * 0.5), 1);
  return geo;
}

function buggyGeometry(w: number, h: number, l: number): THREE.BufferGeometry {
  return new THREE.BoxGeometry(w * 0.95, h, l, 2, 1, 2);
}

function makeSpokeRimGeo(r: number, spokeCount: number): THREE.BufferGeometry {
  const rimSegs = 24;
  const positions = new Float32Array((rimSegs * 2 + spokeCount * 2) * 3);
  let idx = 0;
  for (let i = 0; i < rimSegs; i++) {
    const a0 = (i / rimSegs) * Math.PI * 2;
    const a1 = ((i + 1) / rimSegs) * Math.PI * 2;
    positions[idx++] = 0; positions[idx++] = Math.sin(a0) * r; positions[idx++] = Math.cos(a0) * r;
    positions[idx++] = 0; positions[idx++] = Math.sin(a1) * r; positions[idx++] = Math.cos(a1) * r;
  }
  for (let i = 0; i < spokeCount; i++) {
    const a = (i / spokeCount) * Math.PI * 2;
    positions[idx++] = 0; positions[idx++] = 0; positions[idx++] = 0;
    positions[idx++] = 0; positions[idx++] = Math.sin(a) * r; positions[idx++] = Math.cos(a) * r;
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geo;
}
